import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { festivalInfo, navItems } from "@/mocks/festival";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!isHome) {
      setActive("");
      return undefined;
    }
    const sections = navItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isHome]);

  const goToSection = (id: string) => {
    setMenuOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const light = !scrolled && !menuOpen && isHome;

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          light
            ? "bg-transparent py-4"
            : "bg-background-50/95 backdrop-blur-md border-b border-background-200/70 py-2"
        }`}
      >
        <div className="w-full px-4 md:px-8 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              if (isHome) window.scrollTo({ top: 0, behavior: "smooth" });
              else navigate("/");
            }}
            className="flex items-center gap-3 cursor-pointer text-left shrink-0"
          >
            <span
              className={`flex h-11 w-11 items-center justify-center rounded-full text-2xl transition-colors duration-500 ${
                light ? "btn-gradient text-white" : "btn-gradient text-white"
              }`}
            >
              <span aria-hidden="true" className="leading-none" style={{ filter: "brightness(0) invert(1)" }}>
                🍇
              </span>
            </span>
            <span className="leading-tight">
              <strong
                className={`block font-heading text-base md:text-lg tracking-tight transition-colors duration-500 ${
                  light ? "text-white" : "text-foreground-950"
                }`}
              >
                {festivalInfo.name}
              </strong>
              <span
                className={`block text-[11px] tracking-[0.18em] transition-colors duration-500 ${
                  light ? "text-white/75" : "text-foreground-500"
                }`}
              >
                2026 · 제27회
              </span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goToSection(item.id)}
                className={`cursor-pointer rounded-full px-4 py-2 text-sm transition-colors duration-300 ${
                  light
                    ? active === item.id
                      ? "text-white bg-white/15"
                      : "text-white/85 hover:text-white hover:bg-white/10"
                    : active === item.id
                      ? "text-primary-700 bg-primary-50"
                      : "text-foreground-700 hover:text-primary-700 hover:bg-primary-50/70"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                navigate("/song");
              }}
              className={`hidden sm:inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                light
                  ? "border border-white/40 bg-white/10 text-white hover:bg-white/20"
                  : "border border-primary-200 bg-background-50 text-primary-700 hover:bg-primary-50"
              }`}
            >
              <i className="ri-mic-line"></i>
              가요제 신청
            </button>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                navigate("/apply");
              }}
              className={`hidden sm:inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                light
                  ? "bg-white text-primary-700 hover:bg-white/90"
                  : "btn-gradient text-white"
              }`}
            >
              <i className="ri-plant-line"></i>
              체험 신청
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="전체 메뉴"
              className={`md:hidden flex h-10 w-10 items-center justify-center rounded-full text-xl cursor-pointer transition-colors ${
                light ? "text-white bg-white/15" : "text-foreground-900 bg-background-100"
              }`}
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMenuOpen(false)}></div>
        <div
          className={`absolute right-0 top-0 h-full w-[80%] max-w-xs bg-background-50 pt-24 pb-8 px-5 overflow-y-auto transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goToSection(item.id)}
                className="cursor-pointer rounded-lg px-4 py-3.5 text-left text-base text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              navigate("/apply");
            }}
            className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-full btn-gradient px-5 py-3.5 text-sm font-medium text-white"
          >
            거봉포도 수확체험 신청
          </button>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              navigate("/song");
            }}
            className="mt-3 w-full cursor-pointer whitespace-nowrap rounded-full btn-gradient-accent px-5 py-3.5 text-sm font-medium text-white"
          >
            거봉가요제 참가 신청
          </button>
          <div className="mt-6 rounded-lg bg-background-100 p-4 text-sm text-foreground-600">
            <p className="font-medium text-foreground-900">{festivalInfo.fullName}</p>
            <p className="mt-1">{festivalInfo.dateText}</p>
            <p>{festivalInfo.venue}</p>
          </div>
        </div>
      </div>
    </>
  );
}